import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as monaco from 'monaco-editor';
import { loader } from '@monaco-editor/react';
import Editor from '@monaco-editor/react';

import { JsonEditorWrapper, JsonEditorContainer } from './styles';
import VariableMentionPopup from '../EmailDesignPage/EmailBuilder/VariableMentionPopup';
import { isFocusInsideVariableMentionUI } from '../../utils/variableMentionFocus';
import { validateJson } from '../../utils/contractHelpers';

loader.config({ monaco });

function initialJsonString(val) {
  if (val) {
    try {
      return JSON.stringify(JSON.parse(val), null, 2);
    } catch (err) {
      return val;
    }
  }
  return val;
}

const JsonMappingEditor = ({
  defaultValue,
  onChange,
  t: _t,
  globalVariables = [],
  updateGlobalVariables,
  relationships = [],
}) => {
  const [jsonString, setJsonString] = useState(() => initialJsonString(defaultValue));
  const [showVariablePopup, setShowVariablePopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const containerRef = useRef(null);
  const targetRef = useRef(null);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const popupVisibleCtxRef = useRef(null);
  const popupAnchorRef = useRef(null);
  /** Keeps popup open state in sync for handlers that run outside React render (Monaco callbacks). */
  const popupUiOpenRef = useRef(false);
  const editorUuid = useRef(`json-editor-${Date.now()}`);
  const isUserEditingRef = useRef(false);
  const lastValidJsonStringRef = useRef(initialJsonString(defaultValue));
  const editingDebounceTimerRef = useRef(null);
  const disposablesRef = useRef([]);

  useEffect(() => {
    return () => {
      if (editingDebounceTimerRef.current) {
        clearTimeout(editingDebounceTimerRef.current);
      }
      disposablesRef.current.forEach((d) => {
        try {
          if (d && typeof d.dispose === 'function') {
            d.dispose();
          }
        } catch (e) {
          /* noop */
        }
      });
      disposablesRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (isUserEditingRef.current) {
      return;
    }

    if (defaultValue) {
      try {
        const stringified = initialJsonString(defaultValue);
        setJsonString(stringified);
        lastValidJsonStringRef.current = stringified;

        const editor = editorRef.current;
        const model = editor?.getModel();
        if (model && model.getValue() !== stringified) {
          model.pushEditOperations(
            [],
            [{ range: model.getFullModelRange(), text: stringified }],
            () => null
          );
        }
      } catch (err) {
        setJsonString('');
      }
    } else {
      setJsonString('{}');
      lastValidJsonStringRef.current = '{}';
      const editor = editorRef.current;
      const model = editor?.getModel();
      if (model && model.getValue() !== '{}') {
        model.pushEditOperations(
          [],
          [{ range: model.getFullModelRange(), text: '{}' }],
          () => null
        );
      }
    }
  }, [defaultValue]);

  useEffect(() => {
    if (popupVisibleCtxRef.current) popupVisibleCtxRef.current.set(showVariablePopup);
  }, [showVariablePopup]);

  const isInsideQuotes = (text, position) => {
    if (position === 0) return false;
    const beforeCursor = text.substring(0, position);
    let doubleQuotes = 0;
    let escaped = false;

    for (let i = 0; i < beforeCursor.length; i++) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (beforeCursor[i] === '\\') {
        escaped = true;
        continue;
      }
      if (beforeCursor[i] === '"') {
        doubleQuotes++;
      }
    }
    return doubleQuotes % 2 === 1;
  };

  const extractSearchTermFromQuotes = (text, cursorPos) => {
    if (!isInsideQuotes(text, cursorPos)) return '';

    const beforeCursor = text.substring(0, cursorPos);
    let lastQuoteIndex = -1;
    let escaped = false;

    for (let i = beforeCursor.length - 1; i >= 0; i--) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (beforeCursor[i] === '\\') {
        escaped = true;
        continue;
      }
      if (beforeCursor[i] === '"') {
        lastQuoteIndex = i;
        break;
      }
    }

    if (lastQuoteIndex === -1) return '';

    return beforeCursor.substring(lastQuoteIndex + 1);
  };

  const getValueQuoteSpan = (text, cursorPos) => {
    if (!isInsideQuotes(text, cursorPos)) return null;
    const beforeCursor = text.substring(0, cursorPos);
    let openingQuoteIndex = -1;
    let escaped = false;
    for (let i = beforeCursor.length - 1; i >= 0; i--) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (beforeCursor[i] === '\\') {
        escaped = true;
        continue;
      }
      if (beforeCursor[i] === '"') {
        openingQuoteIndex = i;
        break;
      }
    }
    if (openingQuoteIndex === -1) return null;
    const afterOpening = text.substring(openingQuoteIndex + 1);
    let closingQuoteIndex = openingQuoteIndex + 1;
    escaped = false;
    for (let i = 0; i < afterOpening.length; i++) {
      if (escaped) {
        escaped = false;
        closingQuoteIndex++;
        continue;
      }
      if (afterOpening[i] === '\\') {
        escaped = true;
        closingQuoteIndex++;
        continue;
      }
      if (afterOpening[i] === '"') {
        closingQuoteIndex = openingQuoteIndex + 1 + i;
        return { openingQuoteIndex, closingQuoteIndex };
      }
      closingQuoteIndex++;
    }
    return { openingQuoteIndex, closingQuoteIndex: text.length };
  };

  const calculatePopupPositionMonaco = useCallback((editor, anchorIndex, container, text) => {
    if (!editor || !container) return null;
    const model = editor.getModel();
    if (!model) return null;
    const content = text ?? model.getValue();
    const safeIndex = Math.min(anchorIndex, Math.max(0, content.length));
    const pos = model.getPositionAt(safeIndex);
    const vp = editor.getScrolledVisiblePosition(pos);
    if (!vp) return null;
    const editorDom = editor.getDomNode();
    if (!editorDom) return null;
    const editorRect = editorDom.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const left = editorRect.left + vp.left - containerRect.left;
    const bottom = editorRect.top + vp.top + vp.height - containerRect.top;
    return { left, bottom, target: targetRef.current };
  }, []);

  const isInValuePosition = (text, position) => {
    if (position === 0 || position >= text.length) return false;

    let colonIndex = -1;
    for (let i = position - 1; i >= 0; i--) {
      if (text[i] === ':') {
        colonIndex = i;
        break;
      }
      if (text[i] === '\n' || text[i] === '{') {
        break;
      }
    }

    if (colonIndex === -1) return false;

    const afterColon = text.substring(colonIndex + 1, position);
    const trimmedAfterColon = afterColon.trim();
    const hasQuote = trimmedAfterColon.startsWith('"');

    const insideQuotes = isInsideQuotes(text, position);

    const quoteIndex = afterColon.indexOf('"');
    const atOpeningQuote =
      quoteIndex !== -1 && position === colonIndex + 1 + quoteIndex + 1;

    return hasQuote && (insideQuotes || atOpeningQuote);
  };

  const hidePopup = useCallback(() => {
    popupUiOpenRef.current = false;
    setShowVariablePopup(false);
    setPopupPosition(null);
    popupAnchorRef.current = null;
  }, []);

  const runPopupLogic = useCallback(
    (newValue, cursorPos) => {
      const editor = editorRef.current;
      if (!editor || !containerRef.current) return;

      if (popupUiOpenRef.current && popupAnchorRef.current) {
        const { spanStart, spanEnd } = popupAnchorRef.current;
        if (cursorPos < spanStart || cursorPos > spanEnd) {
          hidePopup();
        }
      }

      const insideQuotes = isInsideQuotes(newValue, cursorPos);
      const inValuePosition = isInValuePosition(newValue, cursorPos);

      if (inValuePosition && containerRef.current) {
        const beforeCursor = newValue.substring(0, cursorPos);
        const lastChar = beforeCursor[beforeCursor.length - 1];
        const justTypedQuote = lastChar === '"';

        if (insideQuotes || justTypedQuote) {
          const searchTermText = extractSearchTermFromQuotes(newValue, cursorPos);
          const span = getValueQuoteSpan(newValue, cursorPos);

          if (span) {
            const anchorIndex = span.openingQuoteIndex + 1;
            const spanStart = anchorIndex;
            const spanEnd = span.closingQuoteIndex;

            const popupOpen = popupUiOpenRef.current;

            if (popupOpen && popupAnchorRef.current) {
              setSearchTerm(searchTermText);
              const position = calculatePopupPositionMonaco(
                editor,
                anchorIndex,
                containerRef.current,
                newValue
              );
              if (position) setPopupPosition(position);
            } else {
              popupAnchorRef.current = { anchorIndex, spanStart, spanEnd };
              const position = calculatePopupPositionMonaco(
                editor,
                anchorIndex,
                containerRef.current,
                newValue
              );
              if (position) {
                setPopupPosition(position);
                setSearchTerm(searchTermText);
                popupUiOpenRef.current = true;
                setShowVariablePopup(true);
              }
            }
          }
        } else {
          hidePopup();
        }
      } else {
        hidePopup();
      }
    },
    [calculatePopupPositionMonaco, hidePopup]
  );

  const runPopupLogicRef = useRef(runPopupLogic);
  runPopupLogicRef.current = runPopupLogic;

  const handleEditorChange = (newValue) => {
    isUserEditingRef.current = true;

    if (editingDebounceTimerRef.current) {
      clearTimeout(editingDebounceTimerRef.current);
    }
    editingDebounceTimerRef.current = setTimeout(() => {
      isUserEditingRef.current = false;
    }, 500);

    setJsonString(newValue);

    if (onChange) {
      let jsonValidationError = null;
      try {
        const validation = validateJson(newValue);
        if (!validation.valid) {
          jsonValidationError = validation.error;
          onChange(null, newValue, jsonValidationError);
          return;
        }
        const parsed = JSON.parse(newValue);
        onChange(parsed, newValue);
      } catch (error) {
        jsonValidationError = error.message;
      }
    }
  };

  const handleEditorMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    monacoRef.current = monacoInstance;

    monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
    });

    const popupVisible = editor.createContextKey('jsonVariablePopupVisible', false);
    popupVisibleCtxRef.current = popupVisible;

    const forwardKeyDown = (keyCode) => {
      const el = targetRef.current;
      if (!el) return;
      const ev = new KeyboardEvent('keydown', { bubbles: true });
      Object.defineProperty(ev, 'keyCode', { value: keyCode, enumerable: true });
      el.dispatchEvent(ev);
    };

    const keyBindings = [
      { code: monacoInstance.KeyCode.UpArrow, keyCode: 38 },
      { code: monacoInstance.KeyCode.DownArrow, keyCode: 40 },
      { code: monacoInstance.KeyCode.Enter, keyCode: 13 },
      { code: monacoInstance.KeyCode.Escape, keyCode: 27 },
    ];

    keyBindings.forEach(({ code, keyCode }) => {
      const disp = editor.addAction({
        id: `json-mapping-popup-key-${keyCode}`,
        label: `Variable popup ${keyCode}`,
        keybindings: [code],
        precondition: 'jsonVariablePopupVisible',
        run: () => {
          forwardKeyDown(keyCode);
        },
      });
      if (disp && typeof disp.dispose === 'function') {
        disposablesRef.current.push(disp);
      }
    });

    const firePopupFromEditor = () => {
      const model = editor.getModel();
      if (!model) return;
      const text = model.getValue();
      const offset = model.getOffsetAt(editor.getPosition());
      runPopupLogicRef.current(text, offset);
    };

    disposablesRef.current.push(editor.onDidChangeModelContent(firePopupFromEditor));
    disposablesRef.current.push(editor.onDidChangeCursorPosition(firePopupFromEditor));
    disposablesRef.current.push(
      editor.onDidScrollChange(() => {
        if (!popupUiOpenRef.current || !popupAnchorRef.current || !containerRef.current) {
          return;
        }
        const { anchorIndex } = popupAnchorRef.current;
        const model = editor.getModel();
        const text = model?.getValue() ?? '';
        const position = calculatePopupPositionMonaco(
          editor,
          anchorIndex,
          containerRef.current,
          text
        );
        if (position) setPopupPosition(position);
      })
    );

    disposablesRef.current.push(
      editor.onDidBlurEditorText(() => {
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (isFocusInsideVariableMentionUI(active)) return;
          hidePopup();
        });
      })
    );
  };

  const handleVariableSelect = (variable) => {
    const editor = editorRef.current;
    const monacoInstance = monacoRef.current;
    if (!editor || !variable || !monacoInstance) return;

    const placeholderText = variable.placeHolderText || variable.name;
    if (!placeholderText) return;

    const model = editor.getModel();
    if (!model) return;

    const jsonText = model.getValue();
    const cursorPos = model.getOffsetAt(editor.getPosition());

    const span = getValueQuoteSpan(jsonText, cursorPos);
    if (!span) return;

    const startOffset = span.openingQuoteIndex + 1;
    const endOffset = span.closingQuoteIndex;

    const startPos = model.getPositionAt(startOffset);
    const endPos = model.getPositionAt(endOffset);

    editor.executeEdits('variable-insert', [
      {
        range: monacoInstance.Range.fromPositions(startPos, endPos),
        text: placeholderText,
        forceMoveMarkers: true,
      },
    ]);

    const offsetAfterClosingQuote = span.openingQuoteIndex + 1 + placeholderText.length + 1;
    const endCursorPos = model.getPositionAt(offsetAfterClosingQuote);
    editor.setPosition(endCursorPos);
    editor.focus();

    popupUiOpenRef.current = false;
    setShowVariablePopup(false);
    popupAnchorRef.current = null;

    const newJsonString = model.getValue();
    setJsonString(newJsonString);

    try {
      const parsed = JSON.parse(newJsonString);
      if (onChange) {
        onChange(parsed, newJsonString);
      }
    } catch (e) {
      /* invalid JSON */
    }
  };

  const handlePopupHide = () => {
    hidePopup();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <JsonEditorContainer
        ref={containerRef}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <JsonEditorWrapper
          style={{
            position: 'relative',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 400,
          }}
        >
          <div
            ref={targetRef}
            tabIndex={-1}
            aria-hidden
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              opacity: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
            data-uuid={editorUuid.current}
          />
          <Editor
            height="100%"
            width="100%"
            defaultLanguage="json"
            language="json"
            theme="vs"
            value={jsonString}
            onChange={handleEditorChange}
            onMount={handleEditorMount}
            options={{
              automaticLayout: true,
              tabSize: 2,
              minimap: { enabled: false },
              wordWrap: 'on',
              autoClosingBrackets: 'always',
              autoClosingQuotes: 'always',
              formatOnPaste: true,
              scrollBeyondLastLine: false,
              fontSize: 14,
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              lineHeight: 22,
              padding: { top: 12, bottom: 12 },
              renderLineHighlight: 'line',
            }}
            loading={<div style={{ padding: 12 }}>Loading editor…</div>}
          />
        </JsonEditorWrapper>
        {showVariablePopup && popupPosition && (
          <VariableMentionPopup
            uuid={editorUuid.current}
            searchTerm={searchTerm}
            onVariableSelect={handleVariableSelect}
            onHide={handlePopupHide}
            position={popupPosition}
            popupPlacement="bottom"
            target={popupPosition.target}
            globalVariables={globalVariables}
            updateGlobalVariables={updateGlobalVariables}
            relationships={relationships}
          />
        )}
      </JsonEditorContainer>
    </div>
  );
};

export default JsonMappingEditor;
